import { useContractStore } from '../contract/useContractStore';
import { useOnboardingStore } from '../onboarding/useOnboardingStore';
import { useUserStore } from '../user/useUserStore';
import { useClientStore } from '../client/useClientStore';
import { useAssetStore } from '../asset/useAssetStore';
import { useClassificationStore } from '../classification/useClassificationStore';
import { useDaySequenceStore } from '../daySequence/useDaySequenceStore';
import { useFavouriteStore } from '../favourite/useFavouriteStore';
import { useJobStore } from '../job/useJobStore';
import { useJobApplicationStore } from '../jobApplication/useJobApplicationStore';
import { usePolicyStore } from '../policy/usePolicyStore';
import { useTenantStore } from '../tenant/useTenantStore';
import {
  useWorkerStore,
  resetWorkersFetchSession,
} from '../worker/useWorkerStore';
import { useWorkerJobStore } from '../workerJob/useWorkerJobStore';
import { useAuditLogStore } from '../auditLog/useAuditLogStore';
import { useTemplateStore } from '../template/useTemplateStore';
import { useInviteStore } from '../invite/useInviteStore';
import { useAiStore } from '../ai/useAiStore';
import { useInboxItemsStore } from '../inboxItem/useInboxItemsStore';
import { useSiteStore } from '../site/useSiteStore';
import { useAvailableWorkersStore } from '../availableWorkers/useAvailableWorkersStore';
import { getApolloClient } from '../lib/getApolloClient';
import { useCoreLoopConfigurationStore } from '../coreLoopConfiguration/useCoreLoopConfigurationStore';
/**
 * Upserts a single item into an array based on the specified key (defaults to 'id').
 * @param array - The array to upsert into
 * @param newItem - The item to upsert
 * @param key - Optional key to use for comparison (defaults to 'id')
 */
export function upsertOne<T extends Record<string, any>>(
  array: T[],
  newItem: T,
  key: keyof T = 'id' as keyof T,
): T[] {
  // Skip items with undefined/null keys to prevent memory corruption
  if (newItem == null || newItem[key] == null) {
    return array;
  }
  const index = array.findIndex((item) => item?.[key] === newItem[key]);
  if (index > -1) {
    // Replace existing
    return [...array.slice(0, index), newItem, ...array.slice(index + 1)];
  }
  // Add new
  return [...array, newItem];
}

/**
 * Upserts multiple items into an array based on the specified key (defaults to 'id').
 * @param array - The array to upsert into
 * @param newItems - The items to upsert
 * @param key - Optional key to use for comparison (defaults to 'id')
 */
export function upsertMany<T extends Record<string, any>>(
  array: T[],
  newItems: T[],
  key: keyof T = 'id' as keyof T,
): T[] {
  // Filter out invalid items from both arrays to prevent memory corruption
  const validArray = array.filter((item) => item != null && item[key] != null);
  const validNewItems = newItems.filter(
    (item) => item != null && item[key] != null,
  );

  const map = new Map(validArray.map((item) => [item[key], item]));
  validNewItems.forEach((item) => {
    map.set(item[key], item);
  });
  return Array.from(map.values());
}

/**
 * Removes a single item from an array based on the specified key (defaults to 'id').
 * @param array - The array to remove from
 * @param itemId - The value to match against the key
 * @param key - Optional key to use for comparison (defaults to 'id')
 */
export function removeOne<T extends Record<string, any>>(
  array: T[],
  itemId: string | number,
  key: keyof T = 'id' as keyof T,
): T[] {
  return array.filter((item) => item?.[key] !== itemId);
}

/**
 * Resets all Zustand stores to their initial states.
 * This function should be called when a user signs out to clear all application state.
 */
export async function resetAllStores(): Promise<void> {
  const AsyncStorage = (
    await import('@react-native-async-storage/async-storage')
  ).default;

  // List of all persisted store storage keys
  const persistedStoreKeys = [
    'user-storage',
    'onboarding-storage',
    'client-storage',
    'tenant-storage',
    'job-storage',
    'asset-store',
    'worker-job-storage',
    'job-applications-storage',
    'worker-storage',
    'policy-storage',
    'day-sequence-job-storage',
    'favourite',
    'classification-storage',
    'inbox-items-storage',
    'site-storage',
    'available-workers-storage',
    'template-storage',
  ];

  // Remove all persisted store data from AsyncStorage
  await Promise.all(
    persistedStoreKeys.map((key) => AsyncStorage.removeItem(key)),
  );

  // Reset onboarding store
  useOnboardingStore.setState({
    onboarding: null,
    userOnboardings: [],
    isLoading: false,
    currentPageId: 0,
  });

  // Reset user store
  useUserStore.setState({
    isLoading: false,
    currentUser: null,
    impersonatedUserId: null,
  });

  // Reset client store
  useClientStore.setState({
    clients: [],
    users: [],
    invites: [],
    hasMore: true,
    lastEvaluatedKey: null,
  });

  // Reset tenant store
  useTenantStore.setState({
    tenantInfo: null,
    tenantResolution: null,
    tenantUsers: [],
    isLoading: false,
    error: null,
  });

  // Reset job store
  useJobStore.setState({
    jobs: [],
    isLoading: false,
  });

  // Reset asset store
  useAssetStore.setState({
    assets: [],
  });

  // Reset worker job store
  useWorkerJobStore.setState({
    isLoading: false,
    errorCode: '',
    errorMessage: '',
    workerJobs: [],
    filterDistance: null,
    filterStartDateFrom: null,
    filterStartDateTo: null,
    shouldRefreshOpenJobs: false,
  });

  // Reset job application store
  useJobApplicationStore.setState({
    jobApplications: [],
    isLoading: false,
    filterStatus: null,
  });

  // Reset worker store
  useWorkerStore.setState({
    workers: [],
    isLoading: false,
    isFetchingMoreWorkers: false,
    error: null,
  });
  resetWorkersFetchSession();

  // Reset policy store
  usePolicyStore.setState({
    policies: [],
  });

  // Reset day sequence store
  useDaySequenceStore.setState({
    byId: {},
    idsByJobId: {},
    page: {},
    inFlight: {},
    staleMs: 2 * 60 * 1000,
    isLoading: false,
  });

  // Reset favourite store
  useFavouriteStore.setState({
    favouriteUsers: [],
    isLoading: false,
    error: null,
  });

  // Reset classification store
  useClassificationStore.setState({
    classificationGroups: [],
    isLoading: false,
  });

  // Reset contract store
  useContractStore.setState({
    contract: null,
  });

  // Reset audit log store
  useAuditLogStore.setState({
    auditLogs: [],
    isLoading: false,
  });

  // Reset template store
  useTemplateStore.setState({
    template: null,
  });

  // Reset invite store
  useInviteStore.setState({
    invites: [],
    isLoading: false,
  });

  // Reset AI store
  useAiStore.setState({
    jobTitle: null,
    jobDescription: null,
    isLoading: false,
    error: null,
  });

  // Reset inbox store
  useInboxItemsStore.setState({
    inboxItems: [],
  });

  // Reset site store
  useSiteStore.setState({
    sites: [],
    isLoading: false,
    error: null,
  });

  // Reset available workers store
  useAvailableWorkersStore.setState({
    availableWorkers: {},
    isLoading: false,
    error: null,
  });
}
export async function resetAllStoresExceptUser(): Promise<void> {
  const AsyncStorage = (
    await import('@react-native-async-storage/async-storage')
  ).default;

  // List of all persisted store storage keys
  const persistedStoreKeys = [
    'onboarding-storage',
    'client-storage',
    'tenant-storage',
    'job-storage',
    'asset-store',
    'worker-job-storage',
    'job-applications-storage',
    'worker-storage',
    'policy-storage',
    'day-sequence-job-storage',
    'favourite',
    'classification-storage',
    'inbox-items-storage',
    'site-storage',
    'available-workers-storage',
    'template-storage',
  ];

  // Remove all persisted store data from AsyncStorage
  await Promise.all(
    persistedStoreKeys.map((key) => AsyncStorage.removeItem(key)),
  );

  // Reset onboarding store
  useOnboardingStore.setState({
    onboarding: null,
    userOnboardings: [],
    isLoading: false,
    currentPageId: 0,
  });

  // Reset client store
  useClientStore.setState({
    clients: [],
    users: [],
    invites: [],
    hasMore: true,
    lastEvaluatedKey: null,
  });

  // Reset tenant store
  useTenantStore.setState({
    tenantInfo: null,
    tenantResolution: null,
    tenantUsers: [],
    isLoading: false,
    error: null,
  });

  // Reset job store
  useJobStore.setState({
    jobs: [],
    isLoading: false,
  });

  // Reset asset store
  useAssetStore.setState({
    assets: [],
  });

  // Reset worker job store
  useWorkerJobStore.setState({
    isLoading: false,
    errorCode: '',
    errorMessage: '',
    workerJobs: [],
    filterDistance: null,
    filterStartDateFrom: null,
    filterStartDateTo: null,
    shouldRefreshOpenJobs: false,
  });

  // Reset job application store
  useJobApplicationStore.setState({
    jobApplications: [],
    isLoading: false,
    filterStatus: null,
  });

  // Reset worker store
  useWorkerStore.setState({
    workers: [],
    isLoading: false,
    isFetchingMoreWorkers: false,
    error: null,
  });
  resetWorkersFetchSession();

  // Reset policy store
  usePolicyStore.setState({
    policies: [],
  });

  // Reset day sequence store
  useDaySequenceStore.setState({
    byId: {},
    idsByJobId: {},
    page: {},
    inFlight: {},
    staleMs: 2 * 60 * 1000,
    isLoading: false,
  });

  // Reset favourite store
  useFavouriteStore.setState({
    favouriteUsers: [],
    isLoading: false,
    error: null,
  });

  // Reset classification store
  useClassificationStore.setState({
    classificationGroups: [],
    isLoading: false,
  });

  // Reset contract store
  useContractStore.setState({
    contract: null,
  });

  // Reset audit log store
  useAuditLogStore.setState({
    auditLogs: [],
    isLoading: false,
  });

  // Reset template store
  useTemplateStore.setState({
    template: null,
  });

  // Reset invite store
  useInviteStore.setState({
    invites: [],
    isLoading: false,
  });

  // Reset AI store
  useAiStore.setState({
    jobTitle: null,
    jobDescription: null,
    isLoading: false,
    error: null,
  });

  // Reset inbox store
  useInboxItemsStore.setState({
    inboxItems: [],
  });

  // Reset site store
  useSiteStore.setState({
    sites: [],
    isLoading: false,
    error: null,
  });

  // Reset available workers store
  useAvailableWorkersStore.setState({
    availableWorkers: {},
    isLoading: false,
    error: null,
  });
}
/**
 * Full sign-out cleanup shared by client and worker apps.
 * Clears Apollo cache and local stores. Auth token is cleared by the caller.
 */
export async function clearSessionOnSignOut(): Promise<void> {
  // Drop currentUser immediately so FCMManager / screens stop issuing requests.
  useUserStore.setState({
    currentUser: null,
    isLoading: false,
    userFetchError: null,
    retryCount: 0,
  });

  try {
    await getApolloClient().clearStore();
  } catch (error) {
    console.error(
      '[clearSessionOnSignOut] Failed to clear Apollo store:',
      error,
    );
  }

  await resetAllStores();
}

/**
 * Drops the in-memory core loop configuration (Zustand state and in-flight
 * fetches) so the next ensure/fetch loads from the network again.
 */
export function clearCoreLoopConfigInMemoryCache(): void {
  useCoreLoopConfigurationStore.getState().clearCoreLoopConfigurationCache();
}
